import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { DbService } from '../../services/db.service';
import { ImageManagerService } from '../../services/image-manager.service';
import { PhotoDialogComponent } from '../../dialogs/photo-dialog/photo-dialog.component';

@Component({
  selector: 'app-detail-photo',
  templateUrl: './detail-photo.component.html',
  styleUrls: ['./detail-photo.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DetailPhotoComponent implements OnInit, OnDestroy {
  operation: any;
  photoCategories: Array<{key: string, label: string, photos: any[], icon: string}> = [];
  
  isDownloading = false;
  downloadProgress = 0;
  downloadStatus = '';

  tasks: any[] = [];

  loading = true;

  readonly tableName = 'smart-pga_tasks';
  readonly collectionName = 'task';

  constructor(
    private router: Router,
    private imageService: ImageManagerService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private db: DbService
  ) {}

  ngOnInit() {
    this.operation = history.state.operation;
    
    if (!this.operation) {
      console.error('Nessun intervento passato al componente detail-photo');
      this.operation = null;
    } else {
        this.db.getTableData(this.tableName, this.collectionName).then((tasks: any[]) => {
            this.tasks = tasks;
            this.organizePhotosByCategory();
        });
    }
  }

  ngOnDestroy() {
    this.cleanupBlobUrls();
  }

  private cleanupBlobUrls() {
    if (this.photoCategories) {
      this.photoCategories.forEach(category => {
        if (category.photos) {
          category.photos.forEach(photo => {
            if (photo.previewUrl && this.isValidBlobUrl(photo.previewUrl)) {
              URL.revokeObjectURL(photo.previewUrl);
            }
          });
        }
      });
    }
  }

  private organizePhotosByCategory() {
    if (!this.operation || !this.operation.photos) {
      return;
    }

    this.photoCategories = [];

    //TODO
    // Definisci le categorie di foto con le loro etichette e icone
    const categoryConfig = [
      { key: 'start', label: 'Foto Iniziale', icon: 'panorama' },
      { key: 'tasks', label: 'Foto operazioni', icon: 'assignment' },
      { key: 'finish', label: 'Foto Finali', icon: 'landscape' },
    ];

    categoryConfig.forEach(category => {
      const photos = this.operation.photos[category.key];
      if (photos && photos.length > 0) {
        // Processa ogni foto per creare l'URL di visualizzazione
        const processedPhotos = photos.map((photo, index) => {
          return this.processPhotoForDisplay(photo, index);
        });

        this.photoCategories.push({
          key: category.key,
          label: category.label,
          photos: processedPhotos,
          icon: category.icon
        });
      }
    });

    this.loading = false;
    this.cdr.markForCheck();

  }

  private getTaskDescription(id_task: number): string {
    if(!id_task) {
      return '';
    }
    const task = this.tasks.find(task => task.id === id_task);
    if (task) {
      return task.descr;
    }
    return '';
  }

  private processPhotoForDisplay(photo: any, index: number): any {
    //TODO da migliorare gestione immagini e unificare

    // Se la foto ha un file ArrayBuffer, convertilo in Blob e crea l'URL
    if (photo.file) {
      const blob = this.imageService.arrayBufferToBlob(photo.file, photo.type);
      const blobUrl = URL.createObjectURL(blob);

      photo.id_task = photo.id_task || null;
      
      return {
        ...photo,
        id_task: photo.id_task,
        descr_task: this.getTaskDescription(photo.id_task),
        displayUrl: this.imageService.generateSafeUrlFromBlob(blob),
        previewUrl: blobUrl, // Salva l'URL per uso futuro
        id: photo.id || index
      };
    }

    // Se la foto è già un Blob, crea direttamente l'URL
    if (photo instanceof Blob) {
      return {
        file: photo,
        displayUrl: this.imageService.generateSafeUrlFromBlob(photo),
        id: index,
      };
    }

    // Fallback: restituisci la foto originale
    return {
      ...photo,
      id: photo.id || index
    };
  }

  private isValidBlobUrl(url: string): boolean {
    try {
      // Verifica se l'URL è un blob URL valido
      return url.startsWith('blob:') && URL.createObjectURL !== undefined;
    } catch (e) {
      return false;
    }
  }


  goBack() {
    this.router.navigate(['/app/dashboard']);
  }

  getTotalPhotos(): number {
    if (!this.operation || !this.operation.photos) {
      return 0;
    }

    let total = 0;
    Object.keys(this.operation.photos).forEach(key => {
      if (this.operation.photos[key] && Array.isArray(this.operation.photos[key])) {
        total += this.operation.photos[key].length;
      }
    });
    return total;
  }

  onImageError(event: any) {
    event.target.style.display = 'none';
    const parent = event.target.parentElement;
    if (parent) {
      parent.innerHTML = `
        <div class="image-error">
          <mat-icon>broken_image</mat-icon>
          <span>Immagine non disponibile</span>
        </div>
      `;
    }
  }

  viewPhoto(photo: any, category: any, descr: string) {
    const dialogRef = this.dialog.open(PhotoDialogComponent, {
      data: { photo, category},
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: 'auto',
      height: 'auto',
      panelClass: 'photo-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Dialog chiuso');
    });
  }

  async downloadAllPhotos() {
    if (this.getTotalPhotos() === 0) {
      console.warn('Nessuna foto da scaricare');
      return;
    }

    if (this.isDownloading) {
      console.log('Download già in corso...');
      return;
    }

    try {
      this.isDownloading = true;
      this.downloadProgress = 0;
      this.downloadStatus = 'Preparazione download...';
      this.cdr.markForCheck();
      
      await this.downloadAllPhoto();
      
      this.downloadStatus = 'Download completato!';
      this.cdr.markForCheck();
      
    } catch (error) {
      console.error('Errore durante il download di tutte le foto:', error);
      this.downloadStatus = 'Errore durante il download';
    } finally {
      this.isDownloading = false;
      // Reset dello stato dopo 3 secondi
      setTimeout(() => {
        this.downloadStatus = '';
        this.downloadProgress = 0;
        this.cdr.markForCheck();
      }, 3000);
    }
  }

  private async downloadAllPhoto() {
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const operationName = this.operation.nome.replace(/[^a-zA-Z0-9]/g, '_') || 'intervento';
    
    let downloadCount = 0;
    const totalPhotos = this.getTotalPhotos();
    
    this.downloadStatus = `Scaricamento ${totalPhotos} foto...`;
    this.cdr.markForCheck();
    
    for (const category of this.photoCategories) {
      for (let i = 0; i < category.photos.length; i++) {
        const photo = category.photos[i];
        const categoryName = category.label.toLowerCase().replace(/\s+/g, '_');
        const fileName = `${operationName}_${categoryName}_${i + 1}_${timestamp}.jpg`;
        
        await this.downloadSinglePhoto(photo, fileName);
        downloadCount++;
        // Aggiorna il progresso
        this.downloadProgress = Math.round((downloadCount / totalPhotos) * 100);
        this.downloadStatus = `Scaricamento foto ${downloadCount}/${totalPhotos}...`;
        this.cdr.markForCheck();
        
        // Piccola pausa per evitare di sovraccaricare il browser
        if (downloadCount < totalPhotos) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }
    
    console.log(`Download completato: ${downloadCount} foto scaricate`);
  }

  private async downloadSinglePhoto(photo: any, fileName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        let blob: Blob;

        // Determina il blob da scaricare
        if (photo.file) {
          blob = this.imageService.arrayBufferToBlob(photo.file, photo.type || 'image/jpeg');
        } else if (photo instanceof Blob) {
          blob = photo;
        } else if (photo.previewUrl) {
          // Crea un blob dall'URL esistente
          fetch(photo.previewUrl)
            .then(response => response.blob())
            .then(blobData => {
              this.downloadBlob(blobData, fileName);
              resolve();
            })
            .catch(error => {
              console.error('Errore nel download della foto:', error);
              reject(error);
            });
          return;
        } else {
          console.error('Impossibile scaricare la foto: formato non supportato');
          reject(new Error('Formato foto non supportato'));
          return;
        }

        this.downloadBlob(blob, fileName);
        resolve();
      } catch (error) {
        console.error('Errore nel download della foto:', error);
        reject(error);
      }
    });
  }

  private downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Pulisci l'URL dopo il download
    URL.revokeObjectURL(url);
  }
}

//TODO, perchè non si aggiorna l UI se non forzo aggiornamento?